// CB:BEGIN admin-homeowner-view
import React, { useState, useEffect } from 'react';
import HomeownerRealDashboard from '../../components/admin/HomeownerRealDashboard';
import { useApi } from '../../lib/useApi';
import { useImpersonation } from '../../context/ImpersonationContext';

interface AdminHomeownerViewProps {
  homeownerId: string;
  onNavigate: (screen: string) => void;
}

const AdminHomeownerView: React.FC<AdminHomeownerViewProps> = ({ homeownerId, onNavigate }) => {
  const api = useApi();
  const { enterSession, isImpersonating, impersonatedUser } = useImpersonation();
  const [homeowner, setHomeowner] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const response = await api.get(`/api/admin/homeowners/${homeownerId}`);
        setHomeowner(response);
      } catch (error) {
        console.error('Error fetching homeowner:', error);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [homeownerId]);

  const handleEnterSession = async () => {
    if (!homeowner?.user?.id) return;
    setSessionLoading(true);
    setSessionError(null);
    try {
      await enterSession(homeowner.user.id);
      // Navigate to the actual homeowner dashboard — admin will see exactly what the homeowner sees
      onNavigate('customer-dashboard');
    } catch (err: any) {
      setSessionError(err.message || 'Failed to enter session');
      setSessionLoading(false);
    }
  };

  const alreadyInThisSession =
    isImpersonating &&
    impersonatedUser?.homeownerId === homeownerId;

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
        <p style={{ marginTop: 16, color: '#6b7280' }}>Loading homeowner...</p>
      </div>
    );
  }

  const name =
    homeowner
      ? `${homeowner.firstName || ''} ${homeowner.lastName || ''}`.trim() ||
        homeowner.user?.email ||
        'Homeowner'
      : 'Homeowner';

  return (
    <div>
      {/* Admin toolbar */}
      <div style={{
        background: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '12px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            className="btn btn-secondary"
            onClick={() => onNavigate('admin-homeowners-list')}
            style={{ fontSize: 13, padding: '6px 14px' }}
          >
            Back to Homeowners
          </button>
          <span style={{ color: '#374151', fontWeight: 600, fontSize: 15 }}>
            {name}
          </span>
          {homeowner?.user?.email && (
            <span style={{ color: '#6b7280', fontSize: 13 }}>{homeowner.user.email}</span>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {sessionError && (
            <span style={{ color: '#dc2626', fontSize: 12 }}>{sessionError}</span>
          )}
          {alreadyInThisSession ? (
            <span style={{
              padding: '6px 12px',
              background: '#fef3c7',
              color: '#92400e',
              borderRadius: 4,
              fontSize: 12,
              fontWeight: 600,
            }}>
              Session active — banner visible above
            </span>
          ) : (
            <button
              className="btn btn-primary"
              onClick={handleEnterSession}
              disabled={sessionLoading || !homeowner?.user?.id}
              style={{ fontSize: 13, padding: '6px 14px' }}
              title="Enter this homeowner's session to take actions on their behalf"
            >
              {sessionLoading ? 'Entering...' : 'Enter Homeowner Session'}
            </button>
          )}
          <button
            className="btn btn-secondary"
            onClick={() => onNavigate(`admin-homeowner-edit-${homeownerId}`)}
            style={{ fontSize: 13, padding: '6px 14px' }}
          >
            Edit Data
          </button>
        </div>
      </div>

      {/* Homeowner Dashboard (admin view — read-only by default) */}
      <HomeownerRealDashboard homeownerId={homeownerId} hideHeader={true} />
    </div>
  );
};

export default AdminHomeownerView;
// CB:END admin-homeowner-view
